import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/admin/stats');
        setStats(res);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load stats');
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="main-container">
        <div className="alert alert-danger">{err}</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="main-container">
        <div className="text-muted">Loading stats…</div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-4 gap-2">
        <div>
          <h1 className="mb-1" style={{ fontWeight: 700 }}>
            <i className="fas fa-user-shield me-2" style={{ color: 'var(--primary)' }}></i>
            Admin dashboard
          </h1>
          <p className="text-muted mb-0">Platform stats and recent activity.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Link to="/admin/test" className="btn btn-outline-primary">
            <i className="fas fa-vial me-1"></i>Prompt testing
          </Link>
          <Link to="/dashboard" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-1"></i>Exit admin
          </Link>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <StatCard icon="fa-users" color="#4f46e5" label="Total users" value={stats.total_users} />
        <StatCard icon="fa-book" color="#0ea5e9" label="Dissertations" value={stats.total_dissertations} />
        <StatCard icon="fa-coins" color="#f59e0b" label="Credits sold" value={stats.total_credits_sold} />
        <StatCard icon="fa-fire" color="#ef4444" label="Credits used" value={stats.total_credits_used} />
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="fas fa-user-plus me-2" style={{ color: 'var(--primary)' }}></i>
                Recent users
              </h5>
              {stats.recent_users.length === 0 ? (
                <p className="text-muted mb-0">No users.</p>
              ) : (
                <div className="list-group list-group-flush">
                  {stats.recent_users.map((u) => (
                    <div
                      key={u.uid}
                      className="list-group-item d-flex justify-content-between align-items-start"
                      style={{ border: 'none', borderBottom: '1px solid var(--border)', padding: '0.75rem 0' }}
                    >
                      <div className="me-2" style={{ minWidth: 0, flex: 1 }}>
                        <div className="fw-semibold text-truncate">{u.full_name || '(no name)'}</div>
                        <div className="text-muted small text-truncate">{u.email}</div>
                      </div>
                      <div className="text-end">
                        <span className="badge bg-warning text-dark">{u.credits ?? 0} cr</span>
                        {u.created_at && (
                          <div className="text-muted small mt-1">{u.created_at.slice(0, 10)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="fas fa-book me-2" style={{ color: 'var(--primary)' }}></i>
                Recent dissertations
              </h5>
              {stats.recent_dissertations.length === 0 ? (
                <p className="text-muted mb-0">No dissertations.</p>
              ) : (
                <div className="list-group list-group-flush">
                  {stats.recent_dissertations.map((d) => (
                    <div
                      key={d.id}
                      className="list-group-item"
                      style={{ border: 'none', borderBottom: '1px solid var(--border)', padding: '0.75rem 0' }}
                    >
                      <div className="fw-semibold text-truncate">{d.topic || '(untitled)'}</div>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span className={`status-badge ${(d.status || 'pending').toLowerCase()}`}>
                          {d.status || 'pending'}
                        </span>
                        {d.created_at && (
                          <span className="text-muted small">{d.created_at.slice(0, 10)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, color, label, value }) {
  return (
    <div className="col-md-6 col-lg-3">
      <div className="card h-100">
        <div className="card-body d-flex align-items-center gap-3">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: `${color}18`,
              color,
              display: 'grid',
              placeItems: 'center',
              fontSize: '1.4rem',
            }}
          >
            <i className={`fas ${icon}`}></i>
          </div>
          <div>
            <div className="text-muted small">{label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{value}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
